import { useEffect } from 'react';

const setMotionOrder = (element: HTMLElement) => {
  Array.from(element.children).forEach((child, index) => {
    if (child instanceof HTMLElement) {
      child.style.setProperty('--as-motion-order', String(Math.min(index, 8)));
    }
  });
};

export const MotionController = ({ path }: { path: string }) => {
  const isArticle = /^\/conseils\/[^/]+\/?$/.test(path);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const motionElements = Array.from(document.querySelectorAll<HTMLElement>('[data-motion]'));
    let revealObserver: IntersectionObserver | undefined;

    motionElements.forEach(setMotionOrder);

    if (reducedMotion || !('IntersectionObserver' in window)) {
      motionElements.forEach((element) => element.classList.add('is-motion-visible'));
    } else {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-motion-visible');
            revealObserver?.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
      );
      motionElements.forEach((element) => revealObserver?.observe(element));
    }

    const progress = document.querySelector<HTMLElement>('.as-reading-progress');
    const tocLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('.as-article-toc a[href^="#"]'));
    const articleSections = tocLinks
      .map((link) => document.getElementById(link.hash.slice(1)))
      .filter((section): section is HTMLElement => Boolean(section));
    let frame = 0;

    const updateReadingState = () => {
      frame = 0;

      if (progress && !reducedMotion) {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;
        progress.style.setProperty('--as-reading-progress', String(ratio));
      }

      if (!articleSections.length) return;
      const marker = Math.min(220, window.innerHeight * 0.3);
      let active = articleSections[0];
      articleSections.forEach((section) => {
        if (section.getBoundingClientRect().top <= marker) active = section;
      });

      tocLinks.forEach((link) => {
        const isActive = link.hash === `#${active.id}`;
        link.classList.toggle('is-active', isActive);
        if (isActive) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    const scheduleReadingState = () => {
      if (!frame) frame = window.requestAnimationFrame(updateReadingState);
    };

    if (progress || tocLinks.length) {
      updateReadingState();
      window.addEventListener('scroll', scheduleReadingState, { passive: true });
      window.addEventListener('resize', scheduleReadingState);
    }

    return () => {
      revealObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleReadingState);
      window.removeEventListener('resize', scheduleReadingState);
    };
  }, [path]);

  return isArticle ? <div className="as-reading-progress" aria-hidden="true" /> : null;
};
